import React from "react";
import Breadcrumb from "components/common/Breadcrumb";
import { useTranslation } from "next-i18next";
import { cn } from "@/common/utils/css.utils";

import Image from "@/components/common/Image";
import { LinkButton } from "@/ui/next/LinkButton";
import SEO from "components/common/seo";
import ProductCarousel from "./ProductCarousel";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";
import { NEW_IN_ROUTE } from "@/common/static/routes.static";
import { MarkdownText } from "@/components/text/LokaliseMarkdownText";

const Icon = ({ icon }) => {
  const { t } = useTranslation("icon-page");

  const {
    seo,
    name,
    title,
    slug,
    mainContent,
    mainImage,
    brands,
    fullContent,
    fullImage,
    secondaryContent,
    secondaryImage,
    ctaImage,
    cta,
  } = icon?.attributes || {};

  const brand = brands?.data?.[0]?.attributes?.slug;

  const renderImage = (image, className, alt) => {
    if (!image || !image.data) {
      return null;
    }
    return (
      <div className={cn("relative overflow-hidden", className)}>
        <Image
          src={image.data.attributes.url}
          layout="fill"
          objectFit="cover"
          alt={`${name} ${alt}`}
        />
      </div>
    );
  };

  const markdownOverrides = {
    a: {
      component: "a",
      props: {
        className: "text-brand-500 hover:underline",
      },
    },
    p: {
      component: Body,
      props: {
        as: "p",
      },
    },
    h2: {
      component: Heading,
      props: {
        as: "h3",
        className: "mb-6",
      },
    },
    h3: {
      component: Heading,
      props: {
        as: "h3",
        className: "mb-6",
      },
    },
  };

  return (
    <div className="px-4">
      <SEO
        title={seo?.title ?? name}
        description={seo?.description}
        image={seo?.image}
      />
      <div className="hidden px-4 py-4 sm:px-0 md:block">
        <Breadcrumb
          items={[
            { label: t("icon-page-breadcrumb"), href: "/icon" },
            { label: name },
          ]}
        />
      </div>
      <div className="grid gap-y-10 px-4 py-4 sm:px-0 md:grid-cols-1 md:gap-y-20">
        <span className="md:-mb-10">
          <Heading as="h1" size="h3" className="mb-3 text-center">
            {name}
          </Heading>
          <Body as="p" className="text-center">
            {title}
          </Body>
        </span>
        <section className="grid gap-x-10 gap-y-6 md:grid-cols-2">
          {renderImage(
            mainImage,
            "h-[15.625rem] md:h-full md:min-h-[25rem] md:oder-last",
            "main image"
          )}
          <div className="flex flex-col gap-y-6 md:order-first">
            <Heading as="h3">
              {t("shop-icon-on-whoppah", { icon: `${name}: ${title}` })}
            </Heading>
            <MarkdownText
              className="prose max-w-none"
              overrides={markdownOverrides}
            >
              {mainContent}
            </MarkdownText>
            {brand && (
              <LinkButton
                href={`/search?brand=${brand}`}
                className="w-full md:max-w-[21.25rem]"
              >
                {t("icon-page-see-all-from-brand", { brand: brand })}
              </LinkButton>
            )}
          </div>
        </section>
        <section className="grid gap-x-10 gap-y-6 md:grid-cols-2">
          {renderImage(
            fullImage,
            "h-[15.625rem] md:h-full md:min-h-[25rem]",
            "full image"
          )}
          <div>
            <MarkdownText
              className="prose max-w-none"
              overrides={markdownOverrides}
            >
              {fullContent}
            </MarkdownText>
          </div>
        </section>
        <section className="grid gap-x-10 gap-y-6 md:grid-cols-2">
          {renderImage(
            secondaryImage,
            "h-[15.625rem] md:h-full md:min-h-[25rem] md:order-last",
            "secondary image"
          )}
          <div>
            <MarkdownText
              className="prose max-w-none"
              overrides={markdownOverrides}
            >
              {secondaryContent}
            </MarkdownText>
          </div>
        </section>
        {cta && (
          <section className="grid w-full grid-cols-1 gap-x-10 gap-y-4 md:mb-6 md:grid-cols-2">
            {renderImage(
              ctaImage,
              "h-[15.625rem] md:h-full md:min-h-[25rem] order-first md:order-last",
              "icon category image"
            )}
            <div className="order-first flex h-full flex-col items-center justify-center space-y-6 md:order-last md:px-24 md:py-6">
              <Heading as="h3" className="text-center">
                {cta?.title}
              </Heading>
              <Body as="p" className="text-center">
                {cta?.description}
              </Body>
              <LinkButton size="large" href={cta?.actionHref ?? NEW_IN_ROUTE}>
                {t("icon-page-see-all")}
              </LinkButton>
            </div>
          </section>
        )}
        <section className="mb-6">
          <ProductCarousel icon={slug} name={t("shop-on-whoppah", { name })} />
        </section>
      </div>
    </div>
  );
};

export default Icon;
